
import { useToast } from "@/hooks/use-toast";
import { useQueryClient } from "@tanstack/react-query";
import { getUserName, logActivity } from "@/utils/userUtils";
import { getSupabaseClient } from "./supabaseClients";
import type { OrderCheckInData } from "./types";

export const useOrderOperations = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const handleOrderCheckIn = async (orderData: OrderCheckInData) => {
    const currentUser = getUserName() || 'Unknown User';
    
    const newOrder = {
      id: `ORD-${Date.now()}`,
      supplier_name: orderData.supplierCode,
      delivery_date: orderData.deliveryDate,
      status: 'active',
      product_count: 0,
      has_credit: false,
      documents: orderData.documents,
      created_by: currentUser
    };

    console.log('🚀 Starting order creation process:', {
      newOrder,
      currentUser,
      timestamp: new Date().toISOString()
    });

    try {
      const client = getSupabaseClient();
      
      console.log('📡 Attempting to insert order into database...');
      const { data, error } = await client
        .from('orders')
        .insert([newOrder])
        .select(); // Add select to get returned data

      if (error) {
        console.error('❌ Database error creating order:', {
          error,
          code: error.code,
          message: error.message,
          details: error.details,
          hint: error.hint
        });
        
        // Provide more specific error messages based on error type
        let errorMessage = "Failed to create order. Please try again.";
        if (error.code === '42501') {
          errorMessage = "Permission denied. Please check your authentication.";
        } else if (error.code === 'PGRST301') {
          errorMessage = "Row level security policy violation. Authentication may have expired.";
        }
        
        toast({
          title: "Order Creation Failed",
          description: errorMessage,
          variant: "destructive"
        });
        return;
      }

      console.log('✅ Order created successfully:', data);
      logActivity('Created new order', { 
        orderId: newOrder.id, 
        supplier: orderData.supplierCode,
        deliveryDate: orderData.deliveryDate,
        success: true
      });

      queryClient.invalidateQueries({ queryKey: ['orders'] });
      
      toast({
        title: "Order Created",
        description: `Order for ${orderData.supplierCode} has been created successfully.`,
      });
    } catch (error) {
      console.error('❌ Unexpected error creating order:', {
        error,
        stack: error instanceof Error ? error.stack : undefined,
        currentUser,
        orderData
      });
      toast({
        title: "System Error",
        description: "An unexpected error occurred. Please contact support if this continues.",
        variant: "destructive"
      });
    }
  };

  return { handleOrderCheckIn };
};
